// Crossfading tiled background.
//
// Two stacked fixed layers, alternating: the incoming tile is set on whichever
// layer is currently hidden, faded up over the visible one, and the old layer
// is then cleared. `background-image` cannot be transitioned directly — it
// only interpolates discretely, hard-flipping at the halfway mark — so the
// crossfade has to be an opacity animation between two elements.
//
// Both layers are `position: fixed`. That is load-bearing, not incidental. The
// tiling used to live on the body with a single fixed layer for the incoming
// tile, but a fixed element anchors its background to the VIEWPORT while the
// body anchors to the DOCUMENT. Once the panels made the page taller than the
// screen, `background-position: center center` resolved to two different
// origins, and handing the image back to the body at the end of each fade
// jumped the pattern by half the overflow — measured at 46px for a 92px
// overflow, about a second after every click. One coordinate space, no handoff.

export const FADE_MS = 600;

const layers = [document.getElementById('bg-a'), document.getElementById('bg-b')];
let visible = 0; // index of the layer currently showing
let transitioning = false;
let currentSrc = null;
let timer = null;

const listeners = new Set();
let position = { x: 0, y: 0 };

function paintPosition() {
  document.body.style.setProperty('--tile-x', `${position.x}px`);
  document.body.style.setProperty('--tile-y', `${position.y}px`);
}

/** Move the shared infinite-canvas viewport. Both crossfade layers inherit
 *  these variables, so changing images never resets or jumps the pattern. */
export function panBackgroundBy(dx, dy) {
  position = {
    x: position.x + Number(dx || 0),
    y: position.y + Number(dy || 0)
  };
  paintPosition();
}

/** Keep the point beneath the cursor stationary while the tile scale changes. */
export function anchorBackgroundScale(oldSize, newSize, clientX, clientY) {
  if (!oldSize || oldSize === newSize) return;
  const ratio = newSize / oldSize;
  const fromCenterX = clientX - window.innerWidth / 2;
  const fromCenterY = clientY - window.innerHeight / 2;
  position = {
    x: fromCenterX - (fromCenterX - position.x) * ratio,
    y: fromCenterY - (fromCenterY - position.y) * ratio
  };
  paintPosition();
}

// One listener throwing must not stop the others, abort the background change
// that triggered it, or — for the immediate call below — break registration.
function call(fn) {
  try {
    fn(currentSrc);
  } catch (err) {
    console.warn('background listener failed:', err);
  }
}

export function onBackgroundChange(fn) {
  listeners.add(fn);
  call(fn);
  return () => listeners.delete(fn);
}

function notify() {
  for (const fn of listeners) call(fn);
}

export function isBackgroundTransitioning() {
  return transitioning;
}

export function currentBackground() {
  return currentSrc;
}

/** Hide the layer that just went out of view, without animating it back out. */
function retire(index) {
  const old = layers[index];
  old.style.transition = 'none';
  old.style.opacity = '0';
  // Restore the transition only after the cleared layer has painted, or the
  // reset itself animates and flashes the old tile.
  requestAnimationFrame(() => {
    old.style.transition = '';
  });
  transitioning = false;
}

export function setBackground(src, { immediate = false } = {}) {
  if (!src || src === currentSrc) return;
  currentSrc = src;
  notify();
  clearTimeout(timer);

  const incoming = 1 - visible;
  const outgoing = visible;
  layers[incoming].style.backgroundImage = `url('${src}')`;
  visible = incoming;

  if (immediate) {
    layers[incoming].style.transition = 'none';
    layers[incoming].style.opacity = '1';
    requestAnimationFrame(() => {
      layers[incoming].style.transition = '';
    });
    retire(outgoing);
    return;
  }

  transitioning = true;
  requestAnimationFrame(() => {
    layers[incoming].style.opacity = '1';
  });
  timer = setTimeout(() => retire(outgoing), FADE_MS);
}
